import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Drawer, Switch } from 'antd';
import type { MenuProps } from 'antd';
import { ReloadOutlined, AppstoreOutlined } from '@ant-design/icons';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';
import UserProfileDropdown from './UserProfileDropdown';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  DashboardOutlined,
  SafetyOutlined,
  GlobalOutlined,
  FileSearchOutlined,
  UserOutlined,
  SettingOutlined,
  BellOutlined,
  AuditOutlined,
  TeamOutlined,
  FileProtectOutlined,
  AlertOutlined,
  SearchOutlined,
  BarChartOutlined,
  ExclamationCircleOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  ApiOutlined,
} from '@ant-design/icons';

const { Header, Content, Sider } = Layout;

// ── Menu item definitions ────────────────────────────────────────────────────

type TabKey =
  | 'overview'
  | 'sanctions'
  | 'sanctions-matching'
  | 'country-risk'
  | 'screenings'
  | 'customers'
  | 'transactions'
  | 'AlertsMonitoring'
  | 'transaction-dashboard'
  | 'reports'
  | 'system-config'
  | 'parameters'
  | 'users'
  | 'roles'
  | 'audit-logs'
  | 'pep'
  | 'due-diligence'
  | 'monitoring-rules-scenarios'
  | 'investigations'
  | 'integrations'
  | 'onboarding'
  | 'fatca'
  | 'unusual-operations'
  | 'approvals'
  | 'alert-suppression'
  | 'ai-reporting'
  | 'kpi-dashboard'
  | 'str-referrals'
  | 'risk-reports';

interface MenuItemDef {
  key: string;
  icon: React.ReactNode;
  labelKey: string;
  category: string;
}

const menuItemDefs: MenuItemDef[] = [
  { key: 'overview', icon: <DashboardOutlined />, labelKey: 'admin.overview', category: 'overview' },
  { key: 'sanctions', icon: <SafetyOutlined />, labelKey: 'admin.sanctionsManagement', category: 'monitoring' },
  { key: 'sanctions-matching', icon: <SearchOutlined />, labelKey: 'admin.sanctionsMatching', category: 'monitoring' },
  { key: 'country-risk', icon: <GlobalOutlined />, labelKey: 'admin.countryRisk', category: 'monitoring' },
  { key: 'screenings', icon: <FileSearchOutlined />, labelKey: 'admin.screeningResults', category: 'monitoring' },
  { key: 'AlertsMonitoring', icon: <BellOutlined />, labelKey: 'admin.alertsMonitoring', category: 'monitoring' },
  { key: 'customers', icon: <UserOutlined />, labelKey: 'admin.customersClassification', category: 'customers' },
  //{ key: 'risk-profiles', icon: <FileProtectOutlined />, labelKey: 'admin.riskProfiles', category: 'customers' },
  { key: 'transactions', icon: <BellOutlined />, labelKey: 'admin.transactionMonitoring', category: 'customers' },
  { key: 'transaction-dashboard', icon: <BarChartOutlined />, labelKey: 'admin.transactionDashboard', category: 'customers' },
  { key: 'unusual-operations', icon: <ExclamationCircleOutlined />, labelKey: 'admin.unusualOperations', category: 'customers' },
  { key: 'pep', icon: <TeamOutlined />, labelKey: 'admin.pepManagement', category: 'customers' },
  { key: 'due-diligence', icon: <FileProtectOutlined />, labelKey: 'admin.dueDiligence', category: 'customers' },
  { key: 'monitoring-rules-scenarios', icon: <AlertOutlined />, labelKey: 'admin.monitoringRulesScenarios', category: 'risk' },
  { key: 'alert-suppression', icon: <SafetyCertificateOutlined />, labelKey: 'admin.alertSuppression', category: 'risk' },
  { key: 'investigations', icon: <SearchOutlined />, labelKey: 'admin.investigations', category: 'risk' },
  { key: 'str-referrals', icon: <FileProtectOutlined />, labelKey: 'admin.strReferrals', category: 'risk' },
  { key: 'risk-reports', icon: <BarChartOutlined />, labelKey: 'admin.riskReports', category: 'risk' },
  { key: 'approvals', icon: <CheckCircleOutlined />, labelKey: 'admin.pendingApprovals', category: 'risk' },
  // { key: 'reports', icon: <FileSearchOutlined />, labelKey: 'admin.reports', category: 'risk' },
  { key: 'ai-reporting', icon: <BarChartOutlined />, labelKey: 'admin.aiReports', category: 'risk' },
  { key: 'kpi-dashboard', icon: <DashboardOutlined />, labelKey: 'admin.kpiDashboard', category: 'risk' },
  { key: 'fatca', icon: <GlobalOutlined />, labelKey: 'admin.fatca', category: 'risk' },
  { key: 'audit-logs', icon: <AuditOutlined />, labelKey: 'admin.auditLogs', category: 'system' },
  { key: 'system-config', icon: <SettingOutlined />, labelKey: 'admin.systemConfiguration', category: 'system' },
  { key: 'parameters', icon: <SettingOutlined />, labelKey: 'admin.systemParameters', category: 'system' },
  { key: 'integrations', icon: <ApiOutlined />, labelKey: 'admin.integrations', category: 'system' },
  { key: 'users', icon: <UserOutlined />, labelKey: 'admin.userManagement', category: 'system' },
  { key: 'roles', icon: <SafetyOutlined />, labelKey: 'admin.roleManagement', category: 'system' },
];

const categoryConfig: Record<string, { labelKey: string; icon: React.ReactNode }> = {
  overview: { labelKey: '', icon: null },
  monitoring: { labelKey: 'admin.menuMonitoring', icon: <SafetyOutlined /> },
  customers: { labelKey: 'admin.menuCustomers', icon: <UserOutlined /> },
  risk: { labelKey: 'admin.menuRiskCompliance', icon: <FileProtectOutlined /> },
  system: { labelKey: 'admin.menuSystemAdmin', icon: <SettingOutlined /> },
};

// ── Route → tab key mapping ──────────────────────────────────────────────────

const routeToTabKey: Record<string, TabKey> = {
  '/admin': 'overview',
  '/admin/sanctions': 'sanctions',
  '/admin/sanctions-matching': 'sanctions-matching',
  '/admin/country-risk': 'country-risk',
  '/admin/screenings': 'screenings',
  '/admin/customers': 'customers',
  '/admin/transactions': 'transactions',
  '/admin/AlertsMonitoring': 'AlertsMonitoring',
  '/admin/transaction-dashboard': 'transaction-dashboard',
  '/admin/reports': 'risk-reports',
  '/admin/system-config': 'system-config',
  '/admin/parameters': 'parameters',
  '/admin/audit-logs': 'audit-logs',
  '/admin/pep': 'pep',
  '/admin/due-diligence': 'due-diligence',
  '/admin/monitoring-rules-scenarios': 'monitoring-rules-scenarios',
  '/admin/investigations': 'investigations',
  '/admin/integrations': 'integrations',
  '/admin/onboarding': 'onboarding',
  '/admin/fatca': 'fatca',
  '/admin/unusual-operations': 'unusual-operations',
  '/admin/approvals': 'approvals',
  '/admin/alert-suppression': 'alert-suppression',
  '/admin/ai-reporting': 'ai-reporting',
  '/admin/kpi-dashboard': 'kpi-dashboard',
  '/admin/str-referrals': 'str-referrals',
};

const tabKeyToRoute: Record<string, string> = {
  overview: '/admin',
  sanctions: '/admin/sanctions',
  'sanctions-matching': '/admin/sanctions-matching',
  'country-risk': '/admin/country-risk',
  screenings: '/admin/screenings',
  customers: '/admin/customers',
  'risk-profiles': '/admin/customers',
  transactions: '/admin/transactions',
  AlertsMonitoring: '/admin/AlertsMonitoring',
  'transaction-dashboard': '/admin/transaction-dashboard',
  'risk-reports': '/admin/reports',
  'system-config': '/admin/system-config',
  parameters: '/admin/parameters',
  'audit-logs': '/admin/audit-logs',
  pep: '/admin/pep',
  'due-diligence': '/admin/due-diligence',
  'monitoring-rules-scenarios': '/admin/monitoring-rules-scenarios',
  investigations: '/admin/investigations',
  integrations: '/admin/integrations',
  onboarding: '/admin/onboarding',
  fatca: '/admin/fatca',
  'unusual-operations': '/admin/unusual-operations',
  approvals: '/admin/approvals',
  'alert-suppression': '/admin/alert-suppression',
  'ai-reporting': '/admin/ai-reporting',
  'kpi-dashboard': '/admin/kpi-dashboard',
  'str-referrals': '/admin/str-referrals',
  reports: '/reports',
  users: '/user-management',
  roles: '/role-management',
};

// ── Component ─────────────────────────────────────────────────────────────────

const MainLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();

  const activeTab: TabKey = routeToTabKey[location.pathname] || 'overview';

  // ── Menu visibility ──────────────────────────────────────────────────────
  const VISIBILITY_KEY = 'AML.SYSTEM_menu_visibility';
  const [menuDrawerOpen, setMenuDrawerOpen] = useState<boolean>(false);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(VISIBILITY_KEY);
      return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const isItemVisible = (key: string): boolean => !hiddenKeys.has(key);

  const toggleItemVisibility = (key: string): void => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const showAllItems = (): void => {
    setHiddenKeys(new Set<string>());
    localStorage.setItem(VISIBILITY_KEY, JSON.stringify([]));
  };

  const hideAllItems = (): void => {
    const all = menuItemDefs.map((m) => m.key);
    setHiddenKeys(new Set(all));
    localStorage.setItem(VISIBILITY_KEY, JSON.stringify(all));
  };

  const resetDefaults = (): void => {
    setHiddenKeys(new Set<string>());
    localStorage.removeItem(VISIBILITY_KEY);
  };

  // ── Build menu tree ───────────────────────────────────────────────────────

  const buildMenuItems = (): MenuProps['items'] => {
    const grouped: Record<string, MenuItemDef[]> = {};
    menuItemDefs.forEach((def) => {
      if (!grouped[def.category]) grouped[def.category] = [];
      grouped[def.category].push(def);
    });

    const result: MenuProps['items'] = [];

    if (grouped['overview']) {
      grouped['overview'].forEach((def) => {
        if (isItemVisible(def.key)) {
          result.push({
            key: def.key,
            icon: def.icon,
            label: t(def.labelKey),
          });
        }
      });
    }

    ['monitoring', 'customers', 'risk', 'system'].forEach((cat) => {
      const items = grouped[cat];
      if (!items) return;
      const visibleChildren = items.filter((def) => isItemVisible(def.key));
      if (visibleChildren.length === 0) return;
      const cfg = categoryConfig[cat];
      result.push({
        key: `group-${cat}`,
        icon: cfg.icon,
        label: t(cfg.labelKey),
        children: visibleChildren.map((def) => ({
          key: def.key,
          label: t(def.labelKey),
        })),
      });
    });

    return result;
  };

  const handleMenuClick = (key: string): void => {
    const route = tabKeyToRoute[key];
    if (route && route !== location.pathname) {
      navigate(route);
    }
  };

  const handleLogout = (): void => {
    logout();
    navigate('/');
  };

  const activeTabLabel = (() => {
    const found = menuItemDefs.find((item) => item.key === activeTab);
    return found ? t(found.labelKey) : t('admin.overview');
  })();

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Layout style={{ minHeight: '100vh', background: '#0A0F1C' }}>
      <Sider
        width={256}
        collapsible
        breakpoint="lg"
        style={{
          background: '#151B2B',
          borderRight: '1px solid #2E3A59',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #2E3A59',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            {/* <h2 style={{ color: '#F1F5F9', margin: 0, fontSize: '20px' }}>{t('app.title')}</h2>
            */}
            <p style={{ color: '#8B9BB4', margin: '8px 0 0 0', fontSize: '12px' }}>{t('app.subtitle')}</p>

          </div>
          <Button
            type="text"
            icon={<AppstoreOutlined style={{ color: '#8B9BB4', fontSize: 18 }} />}
            onClick={() => setMenuDrawerOpen(true)}
            style={{ color: '#8B9BB4' }}
            title={t('admin.menuConfig')}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <style>{`
            .admin-sidebar-menu.ant-menu-dark .ant-menu-submenu-open,
            .admin-sidebar-menu.ant-menu-dark .ant-menu-submenu-active,
            .admin-sidebar-menu.ant-menu-dark .ant-menu-submenu-selected,
            .admin-sidebar-menu.ant-menu-dark .ant-menu-submenu-title:hover {
              background: rgba(0, 82, 255, 0.06) !important;
            }
            .admin-sidebar-menu.ant-menu-dark .ant-menu-sub {
              background: rgba(0, 0, 0, 0.2) !important;
            }
            .admin-sidebar-menu.ant-menu-dark .ant-menu-item-selected {
              background: rgba(0, 82, 255, 0.12) !important;
              border-left: 3px solid #0052FF !important;
            }
            .admin-sidebar-menu.ant-menu-dark .ant-menu-item:hover {
              background: rgba(0, 82, 255, 0.06) !important;
            }
            .admin-sidebar-menu.ant-menu-dark.ant-menu-inline .ant-menu-item::after {
              border-right-color: #0052FF !important;
            }
          `}</style>
          <Menu
            className="admin-sidebar-menu"
            theme="dark"
            mode="inline"
            selectedKeys={[activeTab]}
            items={buildMenuItems()}
            style={{ background: 'transparent', borderRight: 'none' }}
            onClick={({ key }: { key: string }) => {
              if (key.startsWith('group-')) return;
              handleMenuClick(key);
            }}
          />
        </div>
      </Sider>
      <Layout>
        <Header
          style={{
            background: '#151B2B',
            padding: '0 24px',
            borderBottom: '1px solid #2E3A59',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1 style={{ color: '#F1F5F9', margin: 0, fontSize: 22, fontWeight: 600 }}>
            {activeTabLabel}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <LanguageSwitcher />
            <NotificationBell />
            <Button
              type="text"
              icon={<ReloadOutlined style={{ color: '#8B9BB4' }} />}
              style={{ color: '#8B9BB4' }}
            />
            <UserProfileDropdown />
          </div>
        </Header>
        <Content style={{ padding: '24px', overflow: 'auto' }}>
          <Outlet />
        </Content>
      </Layout>

      {/* Menu configuration drawer */}
      <Drawer
        title={t('admin.menuConfig')}
        placement="right"
        onClose={() => setMenuDrawerOpen(false)}
        open={menuDrawerOpen}
        width={360}
        styles={{ body: { background: '#151B2B', padding: '20px' } }}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Button size="small" onClick={showAllItems}>{t('admin.showAll')}</Button>
          <Button size="small" onClick={hideAllItems}>{t('admin.hideAll')}</Button>
          <Button size="small" onClick={resetDefaults}>{t('admin.resetDefaults')}</Button>
        </div>
        {menuItemDefs.map((item) => (
          <div
            key={item.key}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: '1px solid #2E3A59',
            }}
          >
            <span style={{ color: '#F1F5F9', fontSize: 14 }}>{t(item.labelKey)}</span>
            <Switch
              checked={isItemVisible(item.key)}
              onChange={() => toggleItemVisibility(item.key)}
              size="small"
            />
          </div>
        ))}
      </Drawer>
    </Layout>
  );
};

export default MainLayout;
